<!--
	@name app-firm
	@description Firm admin dashboard
	@date 2020/07/10
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name" class="pa-3">
		<v-sheet class="pa-6 mb-9 rounded background-gradient">
			<v-row no-gutters>
				<v-col class="shrink align-self-center">
					<div class="align-self-center nowrap headline white--text">
						{{ displayTitle }}
					</div>
				</v-col>
				<v-col class="grow text-right">
					<common-dialog-glossary class="d-inline" :items="glossaryItems" :button-text-hidden="true" :icon-large="true" icon-color="white" title="Glossary" />

					<v-tooltip v-if="!restrictedRoutes.includes('myCompany.webhooks')" bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" :loading="loadingApiCredentials" @click="openApiCredentials" v-on="on">
								<v-icon large>
									mdi-api
								</v-icon>
							</v-btn>
						</template>
						<span>API Credentials</span>
					</v-tooltip>

					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" @click="openInvite" v-on="on">
								<v-icon large>
									mdi-account-plus
								</v-icon>
							</v-btn>
						</template>
						<span>Invite New Team Member</span>
					</v-tooltip>
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-btn large text color="white" to="/account" v-on="on">
								<v-icon large>
									mdi-backburger
								</v-icon>
							</v-btn>
						</template>
						<span>Back to Dashboard</span>
					</v-tooltip>
				</v-col>
			</v-row>
		</v-sheet>

		<v-row class="mb-10">
			<v-col class="py-0" cols="12" md="10">
				<div class="border-effect border-effect--lg ml-6">
					<p class="text-h5 mb-3 font-weight-regular">
						Account name: <span class="font-weight-bold">{{ organisation.name }}</span>
					</p>
					<p class="text-body-1 mb-0" v-html="$sanitize(displayCopy)" />
				</div>
			</v-col>
		</v-row>

		<v-sheet v-if="path === '/firm'" class="px-6 py-3" rounded="" white>
			<v-row>
				<v-col class="py-0" cols="12">
					<v-row>
						<v-col v-for="(section, i) in filteredSections" :key="i" cols="12" md="4">
							<v-item>
								<v-card
									class="d-flex flex-grow-1 flex-column h-100"
									color="grey lighten-2"
									style="flex-basis: 30%; position: relative"
									hover
									@click="$router.push(section.path)"
								>
									<v-system-bar :class="`${section.cardClass} background-rotation-manual--${(i % 5) + 1}`" height="16" />
									<div class="d-flex flex-column justify-left align-center flex-grow-1 text-center pa-6">
										<p class="text-h6 mb-2" v-html="$sanitize(section.text)" />
										<p class="text-body-2 mb-0" v-html="$sanitize(section.abstract)" />
									</div>
								</v-card>
							</v-item>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
		</v-sheet>
		<firm-accounts v-else-if="path === '/firm/accounts'" />
		<firm-details v-else-if="path === '/firm/details'" />
		<firm-domains v-else-if="path === '/firm/domains'" />
		<firm-trading-styles v-else-if="path === '/firm/trading-styles'" />
		<firm-panels v-else-if="path === '/firm/panels'" />
		<firm-webhooks v-else-if="path === '/firm/webhooks'" />
		<firm-fact-find-templates v-else-if="path === '/firm/fact-find-templates'" />
		<firm-writeroute v-else-if="path === '/firm/writeroute'" />

		<app-firm-dialog-invite ref="invite" :form="form('firm_invite').value" />
		<common-dialog ref="apiCredentialsDialog" :max-width="800">
			<template #header>
				API Credentials
			</template>
			<template #body>
				<p class="text-body-2">
					Manage credentials to interact with the {{ site.softwareName }} Restful API. Our swagger documenttion can be found at
					<a :href="swaggerDocs" target="_blank">{{ swaggerDocs }}</a>.
				</p>
				<div>
					<v-btn v-if="!Object.keys(apiCredentialsFormData).length" :loading="loadingApiCredentials" color="primary" @click="createApiCredentials">
						Generate credentials
					</v-btn>
					<dynamic-form v-else :form-schema="apiCredentialsFormSchema" :form-data="apiCredentialsFormData" :submit-button="false" />
				</div>
			</template>
		</common-dialog>
	</div>
</template>

<script>
	import { mapActions, mapGetters, mapState } from 'vuex';
	import { ElementTools } from '@/utils';
	import CommonDialog from '@/component/common/dialog';
	import CommonDialogGlossary from '@/component/common/dialog/glossary';
	import AppFirmDialogInvite from '@/component/app/firm/dialog/invite';
	import DynamicForm from '@/component/common/base/dynamic-form';
	import FirmAccounts from '@/component/app/firm/account-list';
	import FirmDetails from '@/component/app/firm/details';
	import FirmDomains from '@/component/app/firm/domains';
	import FirmTradingStyles from '@/component/app/firm/trading-styles';
	import FirmPanels from '@/component/app/firm/panels';
	import FirmWebhooks from '@/component/app/firm/webhooks';
	import FirmFactFindTemplates from '@/component/app/firm/fact-find-templates';
	import FirmWriteroute from '@/component/app/firm/writeroute';

	export default {
		name: 'app-firm',

		components: {
			CommonDialog,
			CommonDialogGlossary,
			AppFirmDialogInvite,
			DynamicForm,
			FirmAccounts,
			FirmDetails,
			FirmDomains,
			FirmTradingStyles,
			FirmPanels,
			FirmWebhooks,
			FirmFactFindTemplates,
			FirmWriteroute
		},

		data() {
			return {
				title: 'My Company',
				copy: 'Here you will find a set of features designed to help you manage your firm account.',
				section: null,
				sections: [
					{
						name: 'myCompany.accounts',
						text: 'Manage User Accounts',
						abstract: 'View all advisers who belong to your firm, add a new adviser, edit, remove or suspend any accounts within your control',
						copy: 'View all advisers who belong to your firm, add a new adviser, edit, remove or suspend any accounts within your control.',
						cardClass: 'white--text',
						path: '/firm/accounts',
						ref: 'sectionOne'
					},
					{
						name: 'myCompany.details',
						text: 'Edit Details',
						abstract: 'Edit firm name, address, phone number and FCA number',
						copy: 'Edit company name, address, phone number and FCA number.',
						cardClass: 'white--text',
						path: '/firm/details',
						ref: 'sectionTwo'
					},
					{
						name: 'myCompany.domains',
						text: 'Domains',
						abstract: 'Manage whitelisted email domains to fast track new account registrations',
						copy: 'Manage whitelisted email domains to fast track new account registrations.',
						cardClass: 'background-rotation-manual--3 white--text',
						path: '/firm/domains',
						ref: 'sectionFour'
					},
					{
						name: 'myCompany.tradingStyles',
						text: 'Trading Styles',
						abstract: 'Manage multiple trading styles under a single account',
						copy: 'Manage multiple trading styles under a single account.',
						cardClass: 'white--text',
						path: '/firm/trading-styles',
						ref: 'sectionThree'
					},
					{
						name: 'myCompany.panels',
						text: 'Panels',
						abstract: 'Build and manage bespoke product panels',
						copy: 'Build and manage bespoke product panels. Use the <strong>preferred</strong> feature to manage panel engagement for your account. Preferred panels will be set as the default choice for all account users on every fresh log in and new sourcing session.',
						cardClass: 'white--text',
						path: '/firm/panels',
						ref: 'sectionSix'
					},
					{
						name: 'myCompany.webhooks',
						text: 'Webhooks',
						abstract: 'Receive real-time data in your CRM system whenever a given event occurs',
						copy: "With webhooks, you can receive real-time data in your CRM system whenever a given event occurs. For example, upon successful completion of a KFI request, your CRM can automatically receive the relevant product data and accompanying documents. <strong>To start benefiting from webhooks: (1) Select an event from the list below. (2) Enter your webhook URL and click submit. That's it!</strong> Your CRM will now receive real-time updates for each of the events for which you have entered a webhook URL.",
						cardClass: 'white--text',
						path: '/firm/webhooks',
						ref: 'sectionFive'
					},
					{
						name: 'myCompany.factFindTemplates',
						text: 'Fact Find Templates',
						abstract: 'Manage templates for your firm. Add a preferred template and set to it to be mandatory or as a default template with the fact find for all users.',
						copy: 'Manage templates for your firm. Add a preferred template and set to it to be mandatory or as a default template with the fact find for all users.',
						cardClass: 'background-rotation-manual--6 white--text',
						path: '/firm/fact-find-templates',
						ref: 'sectionEight'
					},
					{
						name: 'myCompany.writeroute',
						text: 'WriteRoute',
						abstract: 'Manage WriteRoute settings for your firm',
						copy: 'Manage WriteRoute settings for your firm.',
						cardClass: 'white--text',
						path: '/firm/writeroute',
						ref: 'sectionSeven'
					}
				],
				glossaryItems: [
					{
						key: 'inviteNewMember',
						type: 'icon',
						icon: 'mdi-account-plus',
						iconColour: 'primary darken-3',
						title: 'Invite New Team member',
						description:
							'This is functionality designed to allow an account owner or admin to send an invitation to a new member of staff to join their organisation/company.'
					},
					{
						key: 'suspendAccount',
						type: 'icon',
						icon: 'mdi-lock',
						iconColour: 'warning',
						title: 'Suspend Account',
						description: 'This offers the account owner or account admin the capability to revoke the access a staff member has to an account under their organization.'
					},
					{
						key: 'cancelInvitiation',
						type: 'icon',
						icon: 'mdi-cancel',
						iconColour: 'red',
						title: 'Cancel Invitation',
						description: "The 'Cancel invitation' functionality allows the account owner or account admin to terminate an invite."
					},
					{
						key: 'resendInvitation',
						type: 'icon',
						icon: 'mdi-email-sync-outline',
						iconColour: 'blue',
						title: 'Resend Invitation',
						description:
							'An invited account will remain in the "Pending" state until they have completed the invitation acceptance process. Should you need to resend an invitation to an account with a status of "Pending", you can do so using the "Resend Invitation" button. Resending an invitation will invalidate any invitations previously sent to that recipient and trigger a new invitation email/link.'
					},
					{
						key: 'createAdmin',
						type: 'icon',
						icon: 'mdi-key-plus',
						iconColour: 'teal',
						title: 'Create Admin',
						description: 'This allows an account to be made an admin of the organisation on the air sourcing platform.'
					}
				],
				swaggerDocs: process.env.VUE_APP_API_URL + '/doc/api/v1/',
				loadingApiCredentials: false,
				apiCredentialsFormSchema: [
					{
						type: 'text',
						name: 'clientId',
						display: 'Client ID',
						readonly: true,
						dense: true,
						sm: 12
					},
					{
						type: 'password',
						name: 'clientSecret',
						display: 'Client Secret',
						readonly: true,
						dense: true,
						sm: 12
					}
				],
				apiCredentialsFormData: {}
			};
		},

		computed: {
			...mapState('Account', ['organisation']),
			...mapState('CmsSite', ['site']),
			...mapGetters('CmsForm', ['form']),
			...mapGetters('CmsSite', ['restrictedRoutes']),

			hasBanner() {
				return this.site.adverts?.advisor;
			},

			path() {
				return this.$route.path;
			},

			filteredSections() {
				return this.sections.filter((section) => !this.restrictedRoutes.includes(section.name));
			},

			breadcrumbs() {
				let bc = [];
				bc.push({ text: 'My Company', exact: true, to: '/firm' });
				if (this.section) bc.push({ text: this.section.text, exact: true });
				return bc;
			},

			displayTitle() {
				if (this.section) return `${this.section.text}`;
				return this.title;
			},

			displayCopy() {
				if (this.section && this.section.copy) return this.section.copy;
				return this.copy;
			}
		},

		watch: {
			path: {
				handler(newVal, prevVal) {
					this.section = null;
					this.setBreadcrumbs([]);
					if (newVal === prevVal || !newVal) return; // Do nothing if already on the destination route or if the destination route is empty
					if (this.sections) this.findSection(this.path);
				}
			}
		},

		created() {
			this.loadForm('firmInvite');
			if (this.sections) this.findSection(this.path);
			if (this.sections) this.findSection(this.path);
		},

		destroyed() {
			this.setBreadcrumbs([]);
		},

		methods: {
			...mapActions('CmsForm', ['loadForm']),
			...mapActions('CmsMenu', ['setBreadcrumbs']),
			...mapActions('AccountOrganisationUserApi', ['getOrganisationApiUser', 'createOrganisationApiUser']),

			findSection(id) {
				// Close dialog if id is empty
				if (!id) this.$refs.section.close();
				let section = this.sections.find((section) => section.path === id);
				if (section) this.openSection(section);
				// else console.log('ERROR'); // TODO handle this
			},

			openSection(section) {
				this.section = section;
				this.setBreadcrumbs(this.breadcrumbs);
			},

			openInvite() {
				this.$refs.invite.open();
			},

			async openApiCredentials() {
				this.$refs.apiCredentialsDialog.open();

				this.loadingApiCredentials = true;

				try {
					this.apiCredentialsFormData = (await this.getOrganisationApiUser(this.organisation.id)).data;
				} catch (error) {
					if (error.status !== 404) ElementTools.fireNotification(this.$el, 'error', 'Failed to load API Credentials');
				}

				this.loadingApiCredentials = false;
			},

			async createApiCredentials() {
				this.loadingApiCredentials = true;

				try {
					this.apiCredentialsFormData = (await this.createOrganisationApiUser(this.organisation.id)).data;
				} catch (error) {
					ElementTools.fireNotification(this.$el, 'error', error.data?.message || 'Failed to create API Credentials');
				}

				this.loadingApiCredentials = false;
			}
		}
	};
</script>
